import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SceneBack } from '@/shared/components/SceneBack';
import { CHARACTERS, findCharacter, portraitFor } from '../data/characters';

/**
 * Watch flow — step 1 of 2: pick two debaters.
 *
 * Once both slots are filled, the pair surfaces and a "Continue →" button
 * routes the user to /arena/watch/topic where they choose the topic. Topic
 * selection used to live here too, but pulling it onto its own screen gives
 * the topic step room for pair-aware suggestions and keeps each screen with
 * a single focal element.
 */
export function Watch() {
  const navigate = useNavigate();
  const [leftId, setLeftId] = useState<string | null>(null);
  const [rightId, setRightId] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'The Stands — The Arena';
  }, []);

  const left = findCharacter(leftId);
  const right = findCharacter(rightId);

  const selectedIds = useMemo(() => {
    const s = new Set<string>();
    if (leftId) s.add(leftId);
    if (rightId) s.add(rightId);
    return s;
  }, [leftId, rightId]);

  const hasBothSelected = leftId !== null && rightId !== null;

  /**
   * Picking logic:
   *   - Already-selected character → clear that slot.
   *   - Otherwise → fill the first empty slot (left first, then right).
   *   - Both slots full → replace right (the most-recently-filled side).
   */
  const handlePick = (id: string) => {
    if (id === leftId) {
      setLeftId(null);
      return;
    }
    if (id === rightId) {
      setRightId(null);
      return;
    }
    if (leftId === null) {
      setLeftId(id);
      return;
    }
    if (rightId === null) {
      setRightId(id);
      return;
    }
    setRightId(id);
  };

  const handleContinue = () => {
    if (!hasBothSelected || !leftId || !rightId) return;
    navigate('/arena/watch/topic', { state: { leftId, rightId } });
  };

  return (
    <main className="scene scene--setup">
      <div
        className="scene__image scene__image--watch"
        role="img"
        aria-label="A roaring crowd packed into the colosseum stands"
      />
      <div className="scene__vignette" />

      <SceneBack />

      <div className="scene__content scene__content--setup">
        <p className="scene__eyebrow">— The Stands —</p>
        <h1 className="scene__title scene__title--setup">Who Will Debate?</h1>

        <ul className="roster" aria-label="Available debaters">
          {CHARACTERS.map((c) => {
            const isSelected = selectedIds.has(c.id);
            return (
              <li key={c.id} className="roster__item">
                <button
                  type="button"
                  className={`roster__btn${isSelected ? ' is-selected' : ''}`}
                  onClick={() => handlePick(c.id)}
                  aria-pressed={isSelected}
                  title={c.blurb}
                >
                  <span className="roster__portrait" aria-hidden="true">
                    <img src={portraitFor(c.id)} alt="" loading="lazy" />
                  </span>
                  <span className="roster__name">{c.name}</span>
                  <span className="roster__era">{c.era}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {hasBothSelected && left && right && (
          <>
            <p className="pair">
              <em className="pair__name">{left.name}</em>
              <span className="pair__divider" aria-hidden="true">
                vs
              </span>
              <em className="pair__name">{right.name}</em>
            </p>

            <button
              type="button"
              className="watch-begin"
              onClick={handleContinue}
            >
              <span className="watch-begin__label">Choose a Topic</span>
              <span aria-hidden="true" className="watch-begin__arrow">
                →
              </span>
            </button>
          </>
        )}
      </div>
    </main>
  );
}
